import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useProfileStore from '../../store/useProfileStore';
import useAuthStore from '../../store/useAuthStore';
import { validatePassword } from '../../utils/passwordValidation';

function Password() {
  const { changePassword, isLoading, error, message, clearError, clearMessage } = useProfileStore();
  const { logout } = useAuthStore();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    old_password: '',
    new_password1: '',
    new_password2: ''
  });

  const [validationError, setValidationError] = useState('');
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation error when user types
    if (validationError) {
      setValidationError('');
    }

    // Update password requirements in real-time for new_password1
    if (name === 'new_password1') {
      setPasswordRequirements({
        length: value.length >= 8,
        uppercase: /[A-Z]/.test(value),
        lowercase: /[a-z]/.test(value),
        number: /\d/.test(value),
        special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value)
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous messages and validation errors
    clearError();
    clearMessage();
    setValidationError('');

    // Check if passwords match
    if (formData.new_password1 !== formData.new_password2) {
      setValidationError('Passwords do not match');
      return;
    }

    // Validate new password meets complex requirements
    const passwordValidation = validatePassword(formData.new_password1);
    if (!passwordValidation.isValid) {
      setValidationError(passwordValidation.errors[0] || 'Password does not meet requirements');
      return;
    }

    try {
      await changePassword(formData);
      // Reset form on success
      setFormData({
        old_password: '',
        new_password1: '',
        new_password2: ''
      });
      setPasswordRequirements({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false
      });
      // Logout user after successful password change
      logout();
      navigate('/login');
    } catch (error) {
      // Error is handled by the store
      console.error('Password change error:', error);
    }
  };

  // Clear messages when component unmounts or when user starts typing
  useEffect(() => {
    return () => {
      clearError();
      clearMessage();
    };
  }, [clearError, clearMessage]);

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold  mb-6">Password change</h2>
      
      {/* Message Display */}
      {message && (
        <div className="mb-4 p-3 rounded-md bg-green-100 text-green-700 border border-green-200">
          {message}
        </div>
      )}
      
      {/* Validation Error Display */}
      {validationError && (
        <div className="mb-4 p-3 rounded-md bg-red-100 text-red-700 border border-red-200">
          {validationError}
        </div>
      )}
      
      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-100 text-red-700 border border-red-200">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        
        {/* Old Password */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Old password
          </label>
          <input
            type="password"
            name="old_password"
            value={formData.old_password}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="Enter old password"
            required
          />
        </div>
        
        {/* New Password */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            New password
          </label>
          <input
            type="password"
            name="new_password1"
            value={formData.new_password1}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="Enter new password"
            required
          />
          
          {/* Password Requirements */}
          {formData.new_password1 && (
            <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
              <p className="text-xs font-medium text-white dark:text-white mb-2">
                Password must contain:
              </p>
              <ul className="space-y-1 text-xs">
                <li className={`flex items-center ${passwordRequirements.length ? 'text-green-600 dark:text-white' : 'text-white dark:text-white'}`}>
                  <span className="mr-2">{passwordRequirements.length ? '✓' : '○'}</span>
                  At least 8 characters
                </li>
                <li className={`flex items-center ${passwordRequirements.uppercase ? 'text-green-600 dark:text-white' : 'text-white dark:text-white'}`}>
                  <span className="mr-2">{passwordRequirements.uppercase ? '✓' : '○'}</span>
                  One uppercase letter (A-Z)
                </li>
                <li className={`flex items-center ${passwordRequirements.lowercase ? 'text-green-600 dark:text-white' : 'text-white dark:text-white'}`}>
                  <span className="mr-2">{passwordRequirements.lowercase ? '✓' : '○'}</span>
                  One lowercase letter (a-z)
                </li>
                <li className={`flex items-center ${passwordRequirements.number ? 'text-green-600 dark:text-white' : 'text-white dark:text-white'}`}>
                  <span className="mr-2">{passwordRequirements.number ? '✓' : '○'}</span>
                  One number (0-9)
                </li>
                <li className={`flex items-center ${passwordRequirements.special ? 'text-green-600 dark:text-white' : 'text-white dark:text-white'}`}>
                  <span className="mr-2">{passwordRequirements.special ? '✓' : '○'}</span>
                  One special character (!@#$%^&*()_+-=[]{}|;:,.&lt;&gt;?)
                </li>
              </ul>
            </div>
          )}
        </div>
        
        {/* Confirm New Password */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Confirm new password
          </label>
          <input
            type="password"
            name="new_password2"
            value={formData.new_password2}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="Confirm new password"
            required
          />
        </div>
        
        {/* Change Button */}
        <div className="pt-4">
          <button 
            type="submit"
            disabled={isLoading}
            className={`w-full px-6 py-2 rounded-md font-black transition-colors shadow-md ${
              isLoading 
                ? 'bg-gray-400 cursor-not-allowed !text-white' 
                : 'bg-[#0E1696] hover:bg-[#0E1696] !text-white'
            }`}
            style={!isLoading ? { color: 'black' } : {}}
          >
            {isLoading ? 'Changing...' : 'Change'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default Password;